import React, { useState, useEffect } from 'react';
import api from './utlis/axiosInstance';
import {
    TextField,
    Button,
    Typography,
    Box,
    CircularProgress,
    Paper,
    Container,
    InputAdornment,
    IconButton,
    Alert,
    Snackbar,
    useTheme,
    useMediaQuery,
    Fade,
    Slide,
    Grid,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    Email as EmailIcon,
    Lock as LockIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Description as ContractIcon,
    Security as SecurityIcon,
    Analytics as AnalyticsIcon,
    AdminPanelSettings as AdminIcon,
    Business as BusinessIcon,
    Shield as ShieldIcon
} from '@mui/icons-material';

import Icon from '../../icon.jpg'; // Update with your actual logo path

const LoginPage = ({ setToken }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [animateForm, setAnimateForm] = useState(false);
    const [userType, setUserType] = useState('');
    const navigate = useNavigate();

    const checkUserType = async (emailValue) => {
        if (!emailValue) {
            setUserType('');
            return;
        }
        try {
            const response = await api.post('/auth/check-user-type', { email: emailValue });
            if (response.data && response.data.userType) {
                setUserType(response.data.userType);
            }
        } catch (err) {
            console.error('Error checking user type:', err);
            setUserType('');
        }
    };

    useEffect(() => {
        setAnimateForm(true);
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!email) {
            setError('Please enter your email address');
            return;
        }

        if (!password) {
            setError('Please enter your password');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await api.post('/auth/login', {
                email,
                password,
            });

            const { token, user, message } = response.data;

            if (!token || !user) {
                throw new Error('Invalid response from server');
            }

            if (setToken) {
                setToken(token);
            }

            localStorage.setItem('authToken', token);
            localStorage.setItem('user', JSON.stringify(user));

            setOpenSnackbar(true);
            console.log('Login successful:', user.name);

            navigate('/', { replace: true });

        } catch (err) {
            console.error('Login failed:', err);

            let errorMessage;
            if (err.response) {
                switch (err.response.status) {
                    case 404:
                        errorMessage = 'Invalid email or password';
                        break;
                    case 401:
                        errorMessage = 'Unauthorized access';
                        break;
                    case 500:
                        errorMessage = 'Server error. Please try again later';
                        break;
                    default:
                        errorMessage = err.response.data?.message || 'Authentication failed';
                }
            } else if (err.request) {
                errorMessage = 'Unable to reach the server. Please check your connection';
            } else {
                errorMessage = 'Unable to make login request. Please try again';
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const adminFeatures = [
        {
            icon: <ContractIcon sx={{ fontSize: 32, color: '#1565c0' }} />,
            title: 'Contract Management',
            description: 'Create, review, and manage contracts with advanced workflow controls'
        },
        {
            icon: <AnalyticsIcon sx={{ fontSize: 32, color: '#1565c0' }} />,
            title: 'Analytics & Reports',
            description: 'Generate comprehensive reports and track contract performance metrics'
        },
        // {
        //     icon: <SecurityIcon sx={{ fontSize: 32, color: '#1565c0' }} />,
        //     title: 'Compliance Monitoring',
        //     description: 'Ensure regulatory compliance with automated monitoring and alerts'
        // }
    ];

    if (isMobile) {
        // Mobile/Tablet Layout
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', flexDirection: 'column', py: 4 }}>
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4, mt: 2 }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 80,
                                height: 80,
                                borderRadius: 2,
                                background: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(10px)',
                                mb: 3,
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                            }}
                        >
                            <AdminIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Box>

                        <Typography
                            variant="h4"
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                mb: 1
                            }}
                        >
                            Contract Management
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'white',
                                fontWeight: 500,
                                mb: 1
                            }}
                        >
                            Administrative Portal
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'rgba(255,255,255,0.8)',
                                mt: 1
                            }}
                        >
                            Secure access for authorized administrators
                        </Typography>
                    </Box>

                    {/* Login Form */}
                    <Slide direction="up" in={animateForm} timeout={600}>
                        <Paper
                            sx={{
                                p: 4,
                                borderRadius: 3,
                                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                backdropFilter: 'blur(20px)',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)'
                            }}
                        >
                            {error && (
                                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleLogin}>
                                <TextField
                                    label="Email  1"
                                    type="email"
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ mb: 2 }}
                                />

                                <TextField
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockIcon color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleClickShowPassword} edge="end">
                                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ mb: 3 }}
                                />

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={loading}
                                    sx={{
                                        py: 2,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                                        mb: 2,
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
                                        }
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Access Admin Portal'}
                                </Button>

                                <Box sx={{ textAlign: 'center' }}>
                                    <Button
                                        onClick={() => navigate('/forgot-password')}
                                        sx={{ textTransform: 'none', color: '#1565c0' }}
                                    >
                                        Forgot password?
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>
                    </Slide>
                </Container>
            </Box>
        );
    }

    // Desktop Layout - Corporate Split Screen
    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', backgroundColor: '#f8fafc' }}>
            {/* Left Panel - Corporate Branding */}
            <Box
                sx={{                    width: '45%',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Corporate Pattern Background */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='10' cy='10' r='2'/%3E%3Ccircle cx='50' cy='50' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                />

                <Fade in={animateForm} timeout={1000}>
                    <Box sx={{ textAlign: 'center', zIndex: 1, maxWidth: '400px' ,}}>
                        {/* Corporate Logo/Icon */}
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',                                width: 70,
                                height: 70,
                                borderRadius: 3,
                                background: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(20px)',
                                mb: 3,
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }}
                        >
                            <img src={Icon} alt="Corporate Logo" style={{ width: '50px', height: '50px',borderRadius:"10px" }} />
                            {/* <BusinessIcon sx={{ fontSize: 60, color: 'white' }} /> */}
                        </Box>

                        {/* Corporate Title */}
                        <Typography
                            variant="h2"
                            sx={{
                                color: 'white',
                                fontWeight: 800,
                                mb: 2,
                                lineHeight: 1.1,
                                fontSize: '1.5rem',
                            }}
                        >
                            Contract Management System
                        </Typography>

                        <Typography
                                 variant="h5"
                            sx={{
                                color: 'rgba(255,255,255,0.9)',
                                mb: 4,
                                fontWeight: 400,
                                lineHeight: 1.4,
                                fontSize:"15px"
                            }}
                        >
                            Enterprise-grade contract lifecycle management for administrators
                        </Typography>

                        {/* Feature Cards */}
                    <Grid container spacing={2}>
                            {adminFeatures.map((feature, index) => (
                                <Grid item xs={12} key={index}>
                                    <Slide
                                        direction="right"
                                        in={animateForm}
                                        timeout={800 + (index * 200)}
                                    >
                                        <Card
                                            sx={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                                                backdropFilter: 'blur(20px)',
                                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                                borderRadius: 2,
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateX(8px)',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.18)',
                                                }
                                            }}
                                        >                            <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                    <Box
                                        sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                            borderRadius: 2,
                                            p: 1,
                                                            mr: 3,
                                                            minWidth: 'fit-content'
                                                        }}
                                                    >
                                                        {React.cloneElement(feature.icon, {sx: { fontSize: 24, color: 'white' }
                                        })}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                color: 'white',
                                                fontWeight: 600,
                                                mb: 0.5
                                            }}
                                        >
                                                            {feature.title}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'rgba(255,255,255,0.8)',
                                                                lineHeight: 1.5
                                                            }}
                                                        >
                                                            {feature.description}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Slide>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Fade>
            </Box>            {/* Right Panel - Login Form */}            
            <Box
                sx={{
                    width: '55%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#ffffff',
                    p: 2,
                    overflow: 'auto'
                }}
            >
                <Box sx={{ width: '100%', maxWidth: '500px' }}>
                    <Slide direction="left" in={animateForm} timeout={600} mountOnEnter unmountOnExit>
                        <Box>
                            <Paper
                                elevation={0}
                                sx={{
                                    width: '100%',
                                    p: 2,
                                    borderRadius: 4,
                                    backgroundColor: 'white',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                {/* Admin Badge */}
                                <Box sx={{ textAlign: 'center', mb: 2 }}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: 20,
                                            backgroundColor: '#dbeafe',
                                            border: '1px solid #bfdbfe',
                                            mb: 1
                                        }}
                                    >
                                        <ShieldIcon sx={{ fontSize: 16, color: '#1e40af', mr: 1 }} />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#1e40af',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: 1
                                            }}
                                        >
                                            Administrator Access
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ textAlign: 'center', mb: 2 }}>
                                    <Box
                                        component="img"
                                        src="https://jobsnprofiles.com/static/media/logo-light-bg.3ca0586747e457941f0d.png"
                                        alt="Jobs N Profiles Logo"
                                        sx={{
                                            width: '160px',
                                            height: 'auto',
                                            marginBottom: '8px'
                                        }}
                                    />
                                </Box>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        mb: 2,
                                        textAlign: 'center',
                                        fontSize: '0.9rem',
                                        color: '#64748b'
                                    }}
                                >
                                    Sign in to access the contract management
                                </Typography>

                                {error && (
                                    <Alert
                                        severity="error"
                                        sx={{
                                            mb: 2,
                                            borderRadius: 2,
                                            border: '1px solid #fecaca',
                                            backgroundColor: '#fef2f2'
                                        }}
                                    >
                                        {error}
                                    </Alert>
                                )}

                                <Box component="form" onSubmit={handleLogin} noValidate>                                    <TextField
                                        label="Email"
                                        type="email"
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            checkUserType(e.target.value);
                                        }}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailIcon sx={{ color: '#64748b' }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: userType && (
                                                <InputAdornment position="end">
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            backgroundColor: '#e2e8f0',
                                                            color: '#1e40af',
                                                            px: 1,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            fontWeight: 600,
                                                            textTransform: 'capitalize'
                                                        }}
                                                    >
                                                        {userType}
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: '#f8fafc',
                                                '&.Mui-focused': {
                                                    backgroundColor: 'white',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#1e40af',
                                                    }
                                                }
                                            }
                                        }}
                                    />

                                    <TextField
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        fullWidth
                                        margin="normal"
                                        variant="outlined"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockIcon sx={{ color: '#64748b' }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={handleClickShowPassword}
                                                        edge="end"
                                                        sx={{ color: '#64748b' }}
                                                    >
                                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: '#f8fafc',
                                                '&.Mui-focused': {
                                                    backgroundColor: 'white',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#1e40af',
                                                    }
                                                }
                                            }
                                        }}
                                    />

                                    <Box sx={{ textAlign: 'right', mb: 2 }}>
                                        <Button
                                            onClick={() => navigate('/forgot-password')}
                                            sx={{
                                                textTransform: 'none',
                                                color: '#1e40af',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(30, 64, 175, 0.1)'
                                                }
                                            }}
                                        >
                                            Forgot password?
                                        </Button>
                                    </Box>

                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        disabled={loading}
                                        sx={{
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '1rem',
                                            background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
                                            boxShadow: '0 4px 20px rgba(30, 58, 138, 0.3)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
                                                boxShadow: '0 6px 30px rgba(30, 58, 138, 0.4)',
                                            }
                                        }}
                                    >
                                        {loading ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={20} sx={{ color: 'white' }} />
                                                <span>Authenticating...</span>
                                            </Box>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AdminIcon sx={{ fontSize: 20 }} />
                                                <span>Access Your Account</span>
                                            </Box>
                                        )}
                                    </Button>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                                        For technical support or access issues
                                    </Typography>
                                    <Button
                                        href="mailto:contracts@jobsnprofiles.com"
                                        sx={{
                                            textTransform: 'none',
                                            color: '#1e40af',
                                            fontWeight: 600,
                                            p: 0,
                                            minWidth: 'auto'
                                        }}
                                    >
                                        Contact IT Support
                                    </Button>
                                </Box>
                            </Paper>
                        </Box>
                    </Slide>
                </Box>

                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={() => setOpenSnackbar(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        severity="success"
                        variant="filled"
                        sx={{ width: '100%', borderRadius: 2 }}
                    >
                        Authentication successful! Accessing admin portal...
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default LoginPage;